<script setup lang="ts">
import LeNetwork from "@dsk/icons/LeNetwork.vue";
import { computed, nextTick, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { RouterLink } from "vue-router";

import { type MenuItem, useHeaderMenuItems } from "@/composables";
import { vMatomoEvent } from "@/directives";
import { MatomoEventAction, MatomoEventCategory, type UserType } from "@/types";

const props = defineProps<{
  networkName: string;
  isNetwork: boolean;
  userType: UserType;
  isPaying: boolean;
  focusIndex: number | null | undefined;
  isEnterpriseAdmin: boolean;
  isEnterpriseDictionaryEditor: boolean;
}>();

const emit = defineEmits(["itemClick"]);

const { t } = useI18n();

const { menuItems } = useHeaderMenuItems(
  props.userType,
  props.isPaying,
  props.isEnterpriseAdmin,
  props.isEnterpriseDictionaryEditor
);

const onMenuItemClick = (item: MenuItem) => {
  emit("itemClick");

  if (item.onClick !== undefined) {
    item.onClick();
  }
};

// Check if the "network block" in the bottom of the dropdown menu should be shown
const showNetworkInfo = computed<boolean>(() => props.isNetwork);

const items = ref<HTMLLIElement[]>([]);
watch(
  () => props.focusIndex,
  async (index) => {
    if (typeof index !== "number") {
      return;
    }

    await nextTick();
    (items.value[index].firstChild as HTMLElement).focus();
  },
  {
    immediate: true
  }
);
</script>

<template>
  <div class="drop-down-container">
    <li v-for="(item, index) in menuItems" :key="index" ref="items">
      <template v-if="item.tag === 'router-link' && item.href">
        <RouterLink
          v-matomo-event:click="{
            category: MatomoEventCategory.Header,
            action: MatomoEventAction.ClickHeaderButton,
            label: `Picked dropdown item '${item.name}'`
          }"
          :to="item.href"
        >
          <Component :is="item.icon" />
          {{ item.text }}
        </RouterLink>
      </template>
      <template v-else>
        <Component
          :is="item.tag"
          v-matomo-event:click="{
            category: MatomoEventCategory.Header,
            action: MatomoEventAction.ClickHeaderButton,
            label: `Picked dropdown item '${item.name}'`
          }"
          :href="item.href"
          @click="onMenuItemClick(item)"
        >
          <Component :is="item.icon" />
          {{ item.text }}
        </Component>
      </template>
    </li>

    <div v-if="showNetworkInfo" class="drop-down-container__bottom">
      <p>{{ t("bottom-text.greeting") }}</p>
      <p>
        <LeNetwork /> <span class="name">{{ props.networkName }}</span>
      </p>
    </div>
  </div>
</template>

<style scoped lang="less">
@import "@dsk/styles/colors.less";

.drop-down-container {
  position: absolute;
  z-index: 1;
  top: 70px; // header height
  right: 0;
  width: 310px;
  padding: 5px 21px;
  border-radius: 0px 0px 4px 4px;
  background: white;
  box-shadow: 0px 11px 12px -7px rgba(32, 43, 33, 0.25);
  color: @colors[@neutrals][500];

  &__bottom {
    padding-top: 14px;
    padding-bottom: 5px;
    border-top: @colors[@secondary][600] solid 1px;

    p {
      display: flex;
      align-items: center;

      .name {
        max-width: 244px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      svg {
        margin-right: 9px;
      }
    }
  }

  li {
    margin-block: 16px;
    list-style: none;

    button {
      display: flex;
      align-items: center;
      border-style: none;
      background-color: transparent;

      svg {
        margin-right: 13px;
        color: @colors[@primary][500];
      }
    }

    a {
      display: flex;
      align-items: center;
      padding-left: 5px;
      color: @colors[@neutrals][500];

      svg {
        margin-right: 13px;
        color: @colors[@primary][500];
      }
    }

    button,
    a {
      width: 100%;
      height: 26px;
      padding: 2px 6px;
      border-radius: 4px;

      &:focus {
        outline: 1px solid @colors[@secondary][600] !important;
        background-color: @colors[@secondary][200];
      }

      &:hover {
        background-color: @colors[@secondary][200];
      }
    }
  }
}
</style>

<i18n lang="json">
{
  "da": {
    "bottom-text": {
      "greeting": "Logget ind via Åbent netværk:"
    }
  },
  "en": {
    "bottom-text": {
      "greeting": "Logged in via Open Network:"
    }
  },
  "de": {
    "bottom-text": {
      "greeting": "Angemeldet über Offenes Netzwerk:"
    }
  }
}
</i18n>
